/**
 * DataImportPage - Unified CSV Import for Clients, Locations, Task Definitions, Crew
 * PROMPT 48: Safe preview → confirm → execute pattern for all data types
 * PROMPT 58: Added SQL Import tab for legacy uTrac database import
 */
import { useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  Upload,
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  SkipForward,
  Plus,
  RefreshCw,
  Loader2,
  History,
  AlertTriangle,
  Info,
  Building2,
  MapPin,
  ClipboardList,
  Users,
  ArrowRight,
  Download,
  Database,
  Table2,
  FileCode,
  Trash2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { cn } from "../lib/utils";
import api, { extractErrorMessage } from "../lib/api";
import { format } from "date-fns";

// Import configurations for each entity type
const IMPORT_TYPES = {
  clients: {
    label: "Clients",
    icon: Building2,
    endpoint: "/clients/import",
    required: ["name"],
    optional: ["email", "phone", "contact_name", "address", "address2", "address3", "address4", "city", "state", "postal_code", "country", "ref", "currency", "vat_rate", "vat_code", "website", "fax", "notes", "logo", "status"],
    modes: {
      create_new: {
        label: "Create New Clients",
        description: "Only create new clients. Skip rows where client name already exists.",
      },
      update_existing: {
        label: "Update Existing Clients",
        description: "Only update existing clients by name. Skip rows where client is not found.",
      },
    },
    columns: ["name", "email", "phone", "postal_code", "country"],
  },
  locations: {
    label: "Locations",
    icon: MapPin,
    endpoint: "/locations/import",
    required: ["name", "city", "country"],
    optional: ["address", "state", "postal_code", "client_name"],
    modes: {
      create_new: {
        label: "Create New Locations",
        description: "Only create new locations. Skip rows where location (name + city) already exists.",
      },
      update_existing: {
        label: "Update Existing Locations",
        description: "Only update existing locations. Skip rows where location is not found.",
      },
    },
    columns: ["name", "city", "country", "address", "client_name"],
  },
  task_definitions: {
    label: "Task Definitions",
    icon: ClipboardList,
    endpoint: "/task-definitions/import",
    required: ["name"],
    optional: ["description", "task_type", "default_charge_rate", "default_charge_unit", "default_pay_rate", "default_pay_unit", "default_hours", "is_active"],
    modes: {
      create_new: {
        label: "Smart Import (Recommended)",
        description: "Creates new tasks, updates existing ones by name. Only updates non-empty fields from CSV.",
      },
      update_existing: {
        label: "Update Only",
        description: "Only update existing tasks by name. Skip rows where task is not found.",
      },
    },
    columns: ["name", "task_type", "default_pay_rate", "default_charge_rate", "default_hours"],
    defaultMode: "create_new",  // Default to smart import
  },
  crew: {
    label: "Crew",
    icon: Users,
    endpoint: "/staff/import",
    required: [],  // v2: No strict required - accept full_name OR first_name+last_name
    optional: [
      // Identity & Contact (accept multiple formats)
      "full_name", "first_name", "last_name", "email", "phone_mobile", "username",
      // Personal Details
      "date_of_birth", "nationality", "address_line_1", "address_city", "address_country",
      // Crew Attributes
      "active", "preference_value", "crew_chief_capable", "notes",
      // Legacy uTrac fields
      "staffprofile", "phone", "mobile"
    ],
    modes: {
      create_new: {
        label: "Create New Crew",
        description: "Create new crew records. Match on email if present, otherwise create new.",
      },
      update_existing: {
        label: "Update Existing Crew",
        description: "Only update existing crew by email. Skip rows where email is not found.",
      },
    },
    columns: ["first_name", "last_name", "email", "username", "active"],
    helpText: "Accepts: First_name + Last_name OR full_name. Email is optional at import.",
  },
};

// Status badge component
function RowStatusBadge({ status }) {
  switch (status) {
    case "create":
      return (
        <Badge className="bg-green-900/50 text-green-300 border-green-700 text-xs">
          <Plus className="h-3 w-3 mr-1" />
          Create
        </Badge>
      );
    case "update":
      return (
        <Badge className="bg-amber-900/50 text-amber-300 border-amber-600 text-xs font-semibold">
          <RefreshCw className="h-3 w-3 mr-1" />
          UPDATE
        </Badge>
      );
    case "skip":
      return (
        <Badge className="bg-yellow-900/50 text-yellow-300 border-yellow-700 text-xs">
          <SkipForward className="h-3 w-3 mr-1" />
          Skip
        </Badge>
      );
    case "error":
      return (
        <Badge className="bg-red-900/50 text-red-300 border-red-700 text-xs">
          <XCircle className="h-3 w-3 mr-1" />
          Error
        </Badge>
      );
    default:
      return null;
  }
}

// Import panel for each entity type
function ImportPanel({ entityType, config }) {
  const [step, setStep] = useState("upload"); // upload, preview, complete
  const [importMode, setImportMode] = useState("create_new");
  const [file, setFile] = useState(null);
  const [csvContent, setCsvContent] = useState("");
  const [uploading, setUploading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const Icon = config.icon;

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please select a CSV file");
      return;
    }

    setFile(selectedFile);
    setUploading(true);

    try {
      const content = await selectedFile.text();
      setCsvContent(content);

      // Preview the import
      const response = await api.post(`${config.endpoint}/preview`, {
        csv_content: content,
        import_mode: importMode,
      });

      setPreview(response.data);
      setStep("preview");
      
      if (response.data.blocking_errors) {
        toast.error("Import has blocking errors. Please fix and retry.");
      } else if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} rows have errors`);
      } else {
        toast.success("Preview ready. Review and confirm to import.");
      }
    } catch (err) {
      toast.error(`Preview failed: ${extractErrorMessage(err)}`);
    } finally {
      setUploading(false);
    }
  };

  const handleModeChange = async (newMode) => {
    setImportMode(newMode);
    
    if (csvContent) {
      setUploading(true);
      try {
        const response = await api.post(`${config.endpoint}/preview`, {
          csv_content: csvContent,
          import_mode: newMode,
        });
        setPreview(response.data);
      } catch (err) {
        toast.error(`Preview failed: ${extractErrorMessage(err)}`);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleExecute = async () => {
    if (!preview || preview.blocking_errors) return;

    setExecuting(true);
    try {
      const response = await api.post(`${config.endpoint}/execute`, {
        csv_content: csvContent,
        import_mode: importMode,
        confirmed: true,
      });

      setResult(response.data);
      setStep("complete");
      
      if (response.data.success) {
        toast.success(`Import complete: ${response.data.created_count} created, ${response.data.updated_count} updated`);
      } else {
        toast.warning(`Import completed with ${response.data.error_count} errors`);
      }
    } catch (err) {
      toast.error(`Import failed: ${extractErrorMessage(err)}`);
    } finally {
      setExecuting(false);
    }
  };

  const handleDownloadErrors = () => {
    if (!preview?.rows) return;
    
    // Filter rows with errors or warnings
    const errorRows = preview.rows.filter(r => r.errors?.length > 0 || r.warnings?.length > 0);
    
    if (errorRows.length === 0) {
      toast.info("No errors or warnings to download");
      return;
    }
    
    // Build CSV content
    const headers = ["Row Number", "Status", ...config.columns, "Errors", "Warnings"];
    const csvRows = [headers.join(",")];
    
    errorRows.forEach(row => {
      const rowData = [
        row.row_number,
        row.status,
        ...config.columns.map(col => {
          const val = row[col];
          if (Array.isArray(val)) return `"${val.join('; ')}"`;
          if (val && typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
            return `"${val.replace(/"/g, '""')}"`;
          }
          return val || "";
        }),
        `"${(row.errors || []).join('; ')}"`,
        `"${(row.warnings || []).join('; ')}"`
      ];
      csvRows.push(rowData.join(","));
    });
    
    // Create and download file
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `import_errors_${entityType}_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Downloaded ${errorRows.length} rows with issues`);
  };

  const handleReset = () => {
    setStep("upload");
    setFile(null);
    setCsvContent("");
    setPreview(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6">
      {/* Import Mode Selection */}
      <div className="space-y-3">
        <Label>Import Mode</Label>
        <Select value={importMode} onValueChange={handleModeChange}>
          <SelectTrigger className="w-full max-w-md" data-testid={`${entityType}-import-mode`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(config.modes).map(([key, mode]) => (
              <SelectItem key={key} value={key}>
                {mode.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          {config.modes[importMode].description}
        </p>
      </div>

      {/* Required/Optional Fields Info */}
      <div className="flex gap-4 text-sm">
        <div>
          <span className="text-muted-foreground">Required: </span>
          <span className="text-red-400">{config.required.join(", ")}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Optional: </span>
          <span className="text-green-400">{config.optional.join(", ")}</span>
        </div>
      </div>

      {/* Step: Upload */}
      {step === "upload" && (
        <Card className="border-dashed">
          <CardContent className="p-8">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="p-4 rounded-full bg-primary/10 mb-4">
                <Upload className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Upload CSV File</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select a CSV file to import {config.label.toLowerCase()}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                data-testid={`${entityType}-file-input`}
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid={`${entityType}-upload-btn`}
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Select File
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step: Preview */}
      {step === "preview" && preview && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="flex flex-wrap gap-4">
            <Card className="flex-1 min-w-[150px]">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-full bg-blue-500/10">
                  <FileText className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{preview.total_rows}</p>
                  <p className="text-xs text-muted-foreground">Total Rows</p>
                </div>
              </CardContent>
            </Card>
            <Card className="flex-1 min-w-[150px]">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-500/10">
                  <Plus className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{preview.create_count}</p>
                  <p className="text-xs text-muted-foreground">To Create</p>
                </div>
              </CardContent>
            </Card>
            <Card className="flex-1 min-w-[150px]">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-full bg-blue-500/10">
                  <RefreshCw className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{preview.update_count}</p>
                  <p className="text-xs text-muted-foreground">To Update</p>
                </div>
              </CardContent>
            </Card>
            <Card className="flex-1 min-w-[150px]">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-full bg-yellow-500/10">
                  <SkipForward className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{preview.skip_count}</p>
                  <p className="text-xs text-muted-foreground">To Skip</p>
                </div>
              </CardContent>
            </Card>
            {preview.error_count > 0 && (
              <Card className="flex-1 min-w-[150px] border-red-500/30">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 rounded-full bg-red-500/10">
                    <XCircle className="h-5 w-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-500">{preview.error_count}</p>
                    <p className="text-xs text-muted-foreground">Errors</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Validation Summary */}
          {preview.validation_summary?.length > 0 && (
            <Alert variant={preview.blocking_errors ? "destructive" : "default"}>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Warnings</AlertTitle>
              <AlertDescription>
                <ul className="list-disc list-inside">
                  {preview.validation_summary.map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Preview Table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Preview ({preview.rows?.length || 0} rows)</CardTitle>
              {/* Action buttons - at top for visibility */}
              <div className="flex gap-3">
                {/* Download errors button */}
                {preview.rows?.some(r => r.errors?.length > 0 || r.warnings?.length > 0) && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleDownloadErrors}
                    data-testid={`${entityType}-download-errors-btn`}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Issues
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={handleReset}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleExecute}
                  disabled={preview.blocking_errors || executing || (preview.create_count === 0 && preview.update_count === 0)}
                  data-testid={`${entityType}-execute-btn`}
                >
                  {executing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Proceed with Import ({preview.create_count + preview.update_count} rows)
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="max-h-[400px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">#</TableHead>
                      <TableHead className="w-24">Status</TableHead>
                      {config.columns.map((col) => (
                        <TableHead key={col} className="capitalize">
                          {col.replace(/_/g, " ")}
                        </TableHead>
                      ))}
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows?.slice(0, 100).map((row, idx) => (
                      <TableRow key={idx} className={cn(
                        row.status === "error" && "bg-red-500/5",
                        row.status === "update" && "bg-amber-500/10 border-l-2 border-l-amber-500"
                      )}>
                        <TableCell>{row.row_number}</TableCell>
                        <TableCell><RowStatusBadge status={row.status} /></TableCell>
                        {config.columns.map((col) => (
                          <TableCell key={col} className="max-w-[200px] truncate text-xs">
                            {Array.isArray(row[col]) ? row[col].join(", ") : row[col] || "—"}
                          </TableCell>
                        ))}
                        <TableCell className="max-w-[300px]">
                          {row.errors?.length > 0 && (
                            <span className="text-red-400 text-xs">{row.errors.join(", ")}</span>
                          )}
                          {row.warnings?.length > 0 && (
                            <span className="text-amber-400 text-xs font-medium">{row.warnings.join(", ")}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {preview.rows?.length > 100 && (
                <p className="text-sm text-muted-foreground text-center mt-2">
                  Showing first 100 of {preview.rows.length} rows
                </p>
              )}
            </CardContent>
          </Card>

          {/* Actions - also at bottom for accessibility */}
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={handleReset}>
              Cancel
            </Button>
            <Button
              onClick={handleExecute}
              disabled={preview.blocking_errors || executing || (preview.create_count === 0 && preview.update_count === 0)}
              data-testid={`${entityType}-execute-btn-bottom`}
            >
              {executing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Execute Import
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step: Complete */}
      {step === "complete" && result && (
        <Card className={cn(
          "border-2",
          result.success ? "border-green-500/30 bg-green-500/5" : "border-yellow-500/30 bg-yellow-500/5"
        )}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              {result.success ? (
                <CheckCircle className="h-12 w-12 text-green-500" />
              ) : (
                <AlertTriangle className="h-12 w-12 text-yellow-500" />
              )}
              <div>
                <h3 className="text-xl font-semibold">
                  {result.success ? "Import Complete" : "Import Completed with Errors"}
                </h3>
                <p className="text-muted-foreground">
                  {file?.name}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <p className="text-2xl font-bold text-green-400">{result.created_count}</p>
                <p className="text-xs text-muted-foreground">Created</p>
              </div>
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <p className="text-2xl font-bold text-blue-400">{result.updated_count}</p>
                <p className="text-xs text-muted-foreground">Updated</p>
              </div>
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <p className="text-2xl font-bold text-yellow-400">{result.skipped_count}</p>
                <p className="text-xs text-muted-foreground">Skipped</p>
              </div>
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <p className="text-2xl font-bold text-red-400">{result.error_count}</p>
                <p className="text-xs text-muted-foreground">Errors</p>
              </div>
            </div>

            {result.errors?.length > 0 && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Errors</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc list-inside text-sm">
                    {result.errors.slice(0, 10).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                    {result.errors.length > 10 && (
                      <li>... and {result.errors.length - 10} more</li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <Button onClick={handleReset} data-testid={`${entityType}-reset-btn`}>
              Import Another File
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Target entities for SQL import mapping - SAFE entities only
const SQL_TARGET_ENTITIES = {
  staff: { label: "Crew", icon: Users },
  clients: { label: "Clients", icon: Building2 },
  locations: { label: "Locations", icon: MapPin },
  task_definitions: { label: "Task Definitions", icon: ClipboardList },
};

// SQL Import Panel Component
function SQLImportPanel() {
  const [step, setStep] = useState("upload"); // upload, discover, select, execute, complete
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [discovery, setDiscovery] = useState(null);
  const [tableSelections, setTableSelections] = useState({});
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith(".sql")) {
      toast.error("Please select a .sql file");
      return;
    }

    setFile(selectedFile);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await api.post("/import/sql/discover", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000, // 2 minute timeout for large files
      });

      setDiscovery(response.data);
      
      // Initialize table selections - only allow SAFE tables
      const selections = {};
      response.data.tables.forEach((table) => {
        const isSafe = table.safety_status === "SAFE";
        selections[table.table_name] = {
          include: isSafe && !!table.suggested_entity && table.suggested_entity !== "staff_composite",
          target_entity: isSafe ? (table.suggested_entity || "") : "",
        };
      });
      setTableSelections(selections);
      
      setStep("select");
      
      // Count SAFE vs UNSAFE tables
      const safeCount = response.data.tables.filter(t => t.safety_status === "SAFE").length;
      const unsafeCount = response.data.tables.filter(t => t.safety_status === "UNSAFE").length;
      toast.success(`Found ${response.data.tables.length} tables (${safeCount} importable, ${unsafeCount} blocked)`);
    } catch (err) {
      console.error("SQL discovery failed:", err);
      toast.error(extractErrorMessage(err) || "Failed to analyze SQL file");
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleTableToggle = (tableName, checked) => {
    setTableSelections((prev) => ({
      ...prev,
      [tableName]: { ...prev[tableName], include: checked },
    }));
  };

  const handleTargetEntityChange = (tableName, entity) => {
    setTableSelections((prev) => ({
      ...prev,
      [tableName]: { ...prev[tableName], target_entity: entity, include: !!entity },
    }));
  };

  const getSelectedTables = () => {
    return Object.entries(tableSelections)
      .filter(([_, sel]) => sel.include && sel.target_entity)
      .map(([table_name, sel]) => ({
        table_name,
        target_entity: sel.target_entity,
        include: true,
      }));
  };

  const handleExecuteImport = async () => {
    const selectedTables = getSelectedTables();
    
    if (selectedTables.length === 0) {
      toast.error("Please select at least one table to import");
      return;
    }

    setExecuting(true);
    setStep("execute");

    try {
      const response = await api.post("/import/sql/execute", {
        file_id: discovery.file_id,
        tables: selectedTables,
      }, {
        timeout: 300000, // 5 minute timeout for large imports
      });

      setResult(response.data);
      setStep("complete");
      toast.success(`Import complete: ${response.data.success_count} rows imported`);
    } catch (err) {
      console.error("SQL import failed:", err);
      toast.error(extractErrorMessage(err) || "Import failed");
      setStep("select");
    } finally {
      setExecuting(false);
    }
  };

  const handleReset = async () => {
    // Cleanup temporary file
    if (discovery?.file_id) {
      try {
        await api.delete(`/import/sql/cleanup/${discovery.file_id}`);
      } catch (err) {
        console.error("Cleanup failed:", err);
      }
    }
    
    setStep("upload");
    setFile(null);
    setDiscovery(null);
    setTableSelections({});
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      {/* Step: Upload */}
      {step === "upload" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              SQL Database Import
            </CardTitle>
            <CardDescription>
              Import data from legacy SQL database exports (MySQL/MariaDB format).
              Upload a .sql file to scan for importable tables.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-amber-500/10 border-amber-500/30">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <AlertTitle className="text-amber-400">SQL Import Guidelines</AlertTitle>
              <AlertDescription className="text-amber-300/80 text-xs">
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Supports MySQL/MariaDB INSERT INTO statements</li>
                  <li>Maximum file size: 100MB</li>
                  <li>Tables will be auto-detected and mapped to EventsFlo entities</li>
                  <li>Financial data (quotes, invoices) cannot be imported</li>
                </ul>
              </AlertDescription>
            </Alert>

            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
                "hover:border-primary/50 hover:bg-primary/5 cursor-pointer",
                uploading && "opacity-50 pointer-events-none"
              )}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".sql"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="sql-file-input"
              />
              {uploading ? (
                <>
                  <Loader2 className="h-12 w-12 mx-auto mb-4 text-primary animate-spin" />
                  <p className="text-sm text-muted-foreground">Analyzing SQL file...</p>
                </>
              ) : (
                <>
                  <FileCode className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="font-medium">Click to upload SQL file</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    or drag and drop your .sql file here
                  </p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step: Select Tables */}
      {step === "select" && discovery && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Table2 className="h-5 w-5" />
              Select Tables to Import
            </CardTitle>
            <CardDescription>
              {discovery.tables.length} tables found in {discovery.file_name} ({formatFileSize(discovery.file_size)})
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Safety Legend */}
            <Alert className="bg-blue-500/10 border-blue-500/30">
              <Info className="h-4 w-4 text-blue-500" />
              <AlertTitle className="text-blue-400">Import Safety Classification</AlertTitle>
              <AlertDescription className="text-blue-300/80 text-xs">
                <div className="flex gap-4 mt-2">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-900/50 text-green-300 border-green-700">SAFE</Badge>
                    <span>Master data - can be imported</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-red-900/50 text-red-300 border-red-700">UNSAFE</Badge>
                    <span>Operational/financial data - blocked</span>
                  </div>
                </div>
              </AlertDescription>
            </Alert>

            {/* Summary */}
            <div className="flex flex-wrap gap-3">
              <Badge variant="outline" className="text-sm">
                {discovery.total_rows.toLocaleString()} total rows
              </Badge>
              <Badge variant="outline" className="text-sm text-green-400 border-green-500/50">
                {discovery.tables.filter(t => t.safety_status === "SAFE").length} importable tables
              </Badge>
              <Badge variant="outline" className="text-sm text-red-400 border-red-500/50">
                {discovery.tables.filter(t => t.safety_status === "UNSAFE").length} blocked tables
              </Badge>
              <Badge variant="outline" className="text-sm text-blue-400 border-blue-500/50">
                {getSelectedTables().length} selected for import
              </Badge>
            </div>

            {/* Table Selection */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Import</TableHead>
                    <TableHead className="w-20">Status</TableHead>
                    <TableHead>Table</TableHead>
                    <TableHead className="text-right">Rows</TableHead>
                    <TableHead>Import As</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {discovery.tables.map((table) => {
                    const selection = tableSelections[table.table_name] || {};
                    const isSafe = table.safety_status === "SAFE";
                    const isUnsafe = table.safety_status === "UNSAFE";
                    
                    return (
                      <TableRow 
                        key={table.table_name}
                        className={cn(isUnsafe && "opacity-60 bg-red-500/5")}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selection.include && isSafe}
                            onCheckedChange={(checked) => handleTableToggle(table.table_name, checked)}
                            disabled={isUnsafe || !selection.target_entity}
                            data-testid={`sql-table-${table.table_name}-checkbox`}
                          />
                        </TableCell>
                        <TableCell>
                          {isSafe ? (
                            <Badge className="bg-green-900/50 text-green-300 border-green-700 text-xs">
                              SAFE
                            </Badge>
                          ) : (
                            <Badge className="bg-red-900/50 text-red-300 border-red-700 text-xs">
                              UNSAFE
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium text-sm">
                              {table.human_readable_name || table.table_name}
                            </div>
                            <div className="font-mono text-xs text-muted-foreground">
                              {table.table_name}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {table.description}
                            </div>
                            {isUnsafe && (
                              <div className="text-xs text-red-400 italic">
                                {table.safety_reason}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {table.row_count.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {isSafe ? (
                            <Select
                              value={selection.target_entity || "_skip"}
                              onValueChange={(value) => handleTargetEntityChange(table.table_name, value === "_skip" ? "" : value)}
                            >
                              <SelectTrigger className="w-[160px]" data-testid={`sql-table-${table.table_name}-target`}>
                                <SelectValue placeholder="Select entity" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_skip">— Skip —</SelectItem>
                                {Object.entries(SQL_TARGET_ENTITIES).map(([key, config]) => (
                                  <SelectItem key={key} value={key}>
                                    <div className="flex items-center gap-2">
                                      <config.icon className="h-4 w-4" />
                                      {config.label}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-xs text-red-400">Blocked</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Import Rule Notice */}
            <Alert className="bg-amber-500/10 border-amber-500/30">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <AlertDescription className="text-amber-300/80 text-xs">
                <strong>Import Rule:</strong> Only one entity type can be imported per run. 
                Select tables that map to the same target entity (e.g., all Crew tables together).
              </AlertDescription>
            </Alert>

            {/* Sample Data Preview (if available) */}
            {discovery.tables.some(t => t.sample_values?.length > 0) && (
              <details className="text-sm">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  View sample data from tables
                </summary>
                <div className="mt-2 space-y-4 max-h-[300px] overflow-y-auto">
                  {discovery.tables
                    .filter(t => t.sample_values?.length > 0)
                    .map((table) => (
                      <div key={table.table_name} className="border rounded p-3">
                        <div className="font-mono text-xs mb-2 text-muted-foreground">
                          {table.table_name}
                        </div>
                        <pre className="text-xs bg-surface-elevated/50 p-2 rounded overflow-x-auto">
                          {JSON.stringify(table.sample_values[0], null, 2)}
                        </pre>
                      </div>
                    ))}
                </div>
              </details>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={handleReset}>
                <Trash2 className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button 
                onClick={handleExecuteImport}
                disabled={getSelectedTables().length === 0}
                data-testid="sql-execute-import-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                Import {getSelectedTables().length} Table{getSelectedTables().length !== 1 ? "s" : ""}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step: Executing */}
      {step === "execute" && (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="h-12 w-12 mx-auto mb-4 text-primary animate-spin" />
            <p className="font-medium">Importing data...</p>
            <p className="text-sm text-muted-foreground mt-1">
              This may take a few minutes for large files
            </p>
          </CardContent>
        </Card>
      )}

      {/* Step: Complete */}
      {step === "complete" && result && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-400">
              <CheckCircle className="h-5 w-5" />
              Import Complete
            </CardTitle>
            <CardDescription>
              Successfully processed {result.file_name}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-surface-elevated/30 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{result.tables_processed}</div>
                <div className="text-xs text-muted-foreground">Tables Processed</div>
              </div>
              <div className="bg-green-500/10 rounded-lg p-4 text-center border border-green-500/30">
                <div className="text-2xl font-bold text-green-400">{result.success_count}</div>
                <div className="text-xs text-muted-foreground">Rows Imported</div>
              </div>
              <div className="bg-yellow-500/10 rounded-lg p-4 text-center border border-yellow-500/30">
                <div className="text-2xl font-bold text-yellow-400">{result.warning_count}</div>
                <div className="text-xs text-muted-foreground">Warnings</div>
              </div>
              <div className="bg-red-500/10 rounded-lg p-4 text-center border border-red-500/30">
                <div className="text-2xl font-bold text-red-400">{result.error_count}</div>
                <div className="text-xs text-muted-foreground">Errors</div>
              </div>
            </div>

            {/* Per-table Results */}
            {result.table_results?.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Table</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead className="text-right">Created</TableHead>
                      <TableHead className="text-right">Errors</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.table_results.map((tr, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono text-sm">{tr.table_name}</TableCell>
                        <TableCell>{tr.target_entity}</TableCell>
                        <TableCell className="text-right text-green-400">{tr.created || 0}</TableCell>
                        <TableCell className="text-right text-red-400">{tr.errors || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <Button onClick={handleReset} data-testid="sql-reset-btn">
              Import Another File
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function DataImportPage() {
  const [activeTab, setActiveTab] = useState("crew");  // Default to Crew tab
  const [importLogs, setImportLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const loadImportLogs = async () => {
    setLogsLoading(true);
    try {
      const [staffLogs, dataLogs] = await Promise.all([
        api.get("/staff/import/logs", { params: { limit: 20 } }),
        api.get("/data-import/logs", { params: { limit: 20 } }),
      ]);
      
      const allLogs = [
        ...staffLogs.data.map(l => ({ ...l, entity_type: "crew" })),
        ...dataLogs.data,
      ].sort((a, b) => new Date(b.imported_at) - new Date(a.imported_at)).slice(0, 30);
      
      setImportLogs(allLogs);
    } catch (err) {
      console.error("Failed to load import logs:", err);
    } finally {
      setLogsLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="data-import-page">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold tracking-tight">Data Import</h1>
        <p className="text-sm text-muted-foreground">
          Import reference data from CSV files with preview and validation
        </p>
      </div>

      {/* Import Order Guidance */}
      <Alert className="bg-blue-500/10 border-blue-500/30">
        <Info className="h-4 w-4 text-blue-500" />
        <AlertTitle className="text-blue-400">Recommended Import Order</AlertTitle>
        <AlertDescription className="text-blue-300/80">
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge variant="outline" className="border-blue-500/50">1. Clients</Badge>
            <ArrowRight className="h-4 w-4 text-blue-500/50" />
            <Badge variant="outline" className="border-blue-500/50">2. Locations</Badge>
            <ArrowRight className="h-4 w-4 text-blue-500/50" />
            <Badge variant="outline" className="border-blue-500/50">3. Task Definitions</Badge>
            <ArrowRight className="h-4 w-4 text-blue-500/50" />
            <Badge variant="outline" className="border-blue-500/50">4. Crew</Badge>
          </div>
          <p className="text-xs mt-2">
            Locations can be linked to Clients. Import Clients first to enable this linking.
            Task Definitions and Crew can be imported independently.
          </p>
        </AlertDescription>
      </Alert>

      {/* Import Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-grid">
          {Object.entries(IMPORT_TYPES).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <TabsTrigger key={key} value={key} className="gap-2" data-testid={`tab-${key}`}>
                <Icon className="h-4 w-4" />
                {config.label}
              </TabsTrigger>
            );
          })}
          <TabsTrigger value="sql" className="gap-2" data-testid="tab-sql">
            <Database className="h-4 w-4" />
            SQL Import
          </TabsTrigger>
          <TabsTrigger value="logs" className="gap-2" data-testid="tab-logs">
            <History className="h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Import Panels */}
        {Object.entries(IMPORT_TYPES).map(([key, config]) => (
          <TabsContent key={key} value={key}>
            <ImportPanel entityType={key} config={config} />
          </TabsContent>
        ))}

        {/* SQL Import Panel */}
        <TabsContent value="sql">
          <SQLImportPanel />
        </TabsContent>

        {/* Import History */}
        <TabsContent value="logs">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Import History</CardTitle>
                <CardDescription>Recent import operations across all data types</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={loadImportLogs} disabled={logsLoading}>
                {logsLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </CardHeader>
            <CardContent>
              {importLogs.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Mode</TableHead>
                      <TableHead className="text-right">Created</TableHead>
                      <TableHead className="text-right">Updated</TableHead>
                      <TableHead className="text-right">Skipped</TableHead>
                      <TableHead className="text-right">Errors</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          {log.imported_at ? format(new Date(log.imported_at), "PPp") : "—"}
                        </TableCell>
                        <TableCell className="capitalize">
                          <Badge variant="outline">
                            {log.entity_type?.replace(/_/g, " ") || "crew"}
                          </Badge>
                        </TableCell>
                        <TableCell className="capitalize">
                          {log.import_mode?.replace(/_/g, " ") || "—"}
                        </TableCell>
                        <TableCell className="text-right text-green-400">
                          {log.created_count}
                        </TableCell>
                        <TableCell className="text-right text-blue-400">
                          {log.updated_count}
                        </TableCell>
                        <TableCell className="text-right text-yellow-400">
                          {log.skipped_count}
                        </TableCell>
                        <TableCell className="text-right text-red-400">
                          {log.error_count}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No import history yet</p>
                  <Button variant="link" onClick={loadImportLogs} className="mt-2">
                    Load History
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
